import { createApi } from "@reduxjs/toolkit/query/react";
import axiosBaseQuery from "../../../axiosBaseQuery";
import { FEDERATION_ENDPOINTS } from "../../../../api/constants";

/**
 * RTK Query API федерации EyeHub.
 *
 * Двусторонние связи между организациями (propose → consent обеих сторон →
 * revoke) и директория пиров (node_id / SPKI-fingerprint / endpoints — без
 * секретов: хаб остаётся слепым релеем). Списки — UnifiedOffsetPagination
 * ({results, count, pageIndex, pageCount, ...}).
 *
 * Контракты, на которых ломался портал:
 *   - `POST federation-links/{id}/consent/` для hub-staff требует ТЕЛО `{org}`
 *     (slug или uuid одной из сторон): `_resolve_consent_org` не может вывести
 *     организацию из профиля сотрудника провайдера и отвечает 400;
 *   - без create/update/delete записей директории `signed_directory_for()`
 *     всегда собирал `entries: []` — кросс-org сценарий (Ц7) не работал.
 */
export const federationApi = createApi({
  reducerPath: "federationApi",
  baseQuery: axiosBaseQuery(),
  tagTypes: ["FederationLink", "DirectoryEntry"],
  endpoints: (builder) => ({
    getFederationLinks: builder.query({
      // Бэкенд фильтрует по [status, org_a, org_b] — «связи организации X»
      // одним запросом не выражаются (нужен OR по двум FK), поэтому стороны
      // передаются явно, а не общим параметром `org`.
      query: ({ limit = 20, offset = 0, status, org_a: orgA, org_b: orgB } = {}) => ({
        url: FEDERATION_ENDPOINTS.LINKS,
        method: "GET",
        params: {
          limit,
          offset,
          ...(status ? { status } : {}),
          ...(orgA ? { org_a: orgA } : {}),
          ...(orgB ? { org_b: orgB } : {}),
        },
      }),
      providesTags: [{ type: "FederationLink", id: "LIST" }],
    }),

    getDirectoryEntries: builder.query({
      query: ({ limit = 50, offset = 0, installation, node_id: nodeId } = {}) => ({
        url: FEDERATION_ENDPOINTS.DIRECTORY_ENTRIES,
        method: "GET",
        params: {
          limit,
          offset,
          ...(installation ? { installation } : {}),
          ...(nodeId ? { node_id: nodeId } : {}),
        },
      }),
      providesTags: [{ type: "DirectoryEntry", id: "LIST" }],
    }),

    proposeFederationLink: builder.mutation({
      query: (data) => ({ url: FEDERATION_ENDPOINTS.LINKS, method: "POST", data }),
      invalidatesTags: [{ type: "FederationLink", id: "LIST" }],
    }),

    /**
     * Консент стороны связи.
     *
     * @param {{id: string, org?: string}} args `org` (slug|uuid) обязателен для
     *        hub-staff — он выбирает, ОТ ИМЕНИ какой организации подаётся консент;
     *        для org-admin тело пустое (сторона выводится из профиля).
     */
    consentFederationLink: builder.mutation({
      query: ({ id, org } = {}) => ({
        url: FEDERATION_ENDPOINTS.LINK_CONSENT(id),
        method: "POST",
        ...(org ? { data: { org } } : {}),
      }),
      invalidatesTags: [{ type: "FederationLink", id: "LIST" }],
    }),

    revokeFederationLink: builder.mutation({
      query: (id) => ({ url: FEDERATION_ENDPOINTS.LINK_REVOKE(id), method: "POST" }),
      invalidatesTags: [{ type: "FederationLink", id: "LIST" }],
    }),

    // === Записи директории (запись — только hub-admin) ===

    /** `{installation, node_id, spki_fingerprint, endpoints:[{kind,url}]}`. */
    createDirectoryEntry: builder.mutation({
      query: (data) => ({
        url: FEDERATION_ENDPOINTS.DIRECTORY_ENTRIES,
        method: "POST",
        data,
      }),
      invalidatesTags: [{ type: "DirectoryEntry", id: "LIST" }],
    }),

    updateDirectoryEntry: builder.mutation({
      query: ({ id, ...patch }) => ({
        url: FEDERATION_ENDPOINTS.DIRECTORY_ENTRY_DETAIL(id),
        method: "PATCH",
        data: patch,
      }),
      invalidatesTags: [{ type: "DirectoryEntry", id: "LIST" }],
    }),

    deleteDirectoryEntry: builder.mutation({
      query: (id) => ({
        url: FEDERATION_ENDPOINTS.DIRECTORY_ENTRY_DETAIL(id),
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "DirectoryEntry", id: "LIST" }],
    }),
  }),
});

export const {
  useGetFederationLinksQuery,
  useGetDirectoryEntriesQuery,
  useProposeFederationLinkMutation,
  useConsentFederationLinkMutation,
  useRevokeFederationLinkMutation,
  useCreateDirectoryEntryMutation,
  useUpdateDirectoryEntryMutation,
  useDeleteDirectoryEntryMutation,
} = federationApi;

export default federationApi;
